/**
 * Recent Activity widget — chronological feed merging RFP intake events
 * and follow-up scan events. Phase 1 source set; voice-note ingestion
 * (Phase 3) will add `note` events here.
 */

import React, { useMemo, useState } from 'react';
import { ActivityEvent } from '../../services/salesApi';
import { formatTimeAgo } from '../../utils/dateFormatters';
import WidgetHeader from './WidgetHeader';
import FilterChipGroup from './FilterChipGroup';

interface Props {
  loading: boolean;
  error: string | null;
  events: ActivityEvent[];
}

const ICONS: Record<ActivityEvent['kind'], string> = {
  rfp_received: '📨',
  rfp_update: '🔁',
  follow_up_scan: '🔎',
};

const KIND_LABEL: Record<ActivityEvent['kind'], string> = {
  rfp_received: 'RFP',
  rfp_update: 'Update',
  follow_up_scan: 'Scan',
};

type FilterKey = 'all' | ActivityEvent['kind'];

const FILTERS: { value: FilterKey; label: string }[] = [
  { value: 'all',             label: 'All' },
  { value: 'rfp_received',    label: 'RFPs' },
  { value: 'rfp_update',      label: 'Updates' },
  { value: 'follow_up_scan',  label: 'Scans' },
];

const RecentActivity: React.FC<Props> = ({ loading, error, events }) => {
  const [filter, setFilter] = useState<FilterKey>('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter((e) => e.kind === filter);
  }, [events, filter]);

  return (
    <div style={styles.container}>
      <WidgetHeader
        title="Recent sales activity"
        subtitle="Inbox events from the last few days"
        right={<FilterChipGroup options={FILTERS} value={filter} onChange={setFilter} />}
      />

      {error && <div style={styles.error}>{error}</div>}

      {!loading && filtered.length === 0 && (
        <div style={styles.empty}>
          {events.length === 0 ? 'No recent activity to show.' : 'No events match the current filter.'}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <ol style={styles.feed}>
          {filtered.map((event) => (
            <li key={event.id} style={styles.item}>
              <div style={styles.icon} aria-hidden="true">{ICONS[event.kind]}</div>
              <div style={styles.body}>
                <div style={styles.titleRow}>
                  <span style={styles.kindTag}>{KIND_LABEL[event.kind]}</span>
                  <span style={styles.title}>{event.title}</span>
                </div>
                {event.detail && <div style={styles.detail}>{event.detail}</div>}
              </div>
              <div style={styles.meta}>
                {event.link && (
                  <a href={event.link} target="_blank" rel="noopener noreferrer" style={styles.link}>
                    Open ↗
                  </a>
                )}
                <span style={styles.time}>{formatTimeAgo(event.timestamp)}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14 },
  feed: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 0',
    borderBottom: '1px solid var(--border-1)',
  },
  icon: { fontSize: 18, lineHeight: 1, marginTop: 2, width: 24, textAlign: 'center' },
  body: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  kindTag: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
    background: 'var(--bg-surface-2)',
    padding: '2px 6px',
    borderRadius: 'var(--radius-pill)',
  },
  title: { fontSize: 13.5, fontWeight: 600, color: 'var(--fg-1)' },
  detail: { fontSize: 12.5, color: 'var(--fg-3)' },
  meta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  time: { fontSize: 11.5, color: 'var(--fg-4)', whiteSpace: 'nowrap' },
  link: {
    color: 'var(--accent)',
    fontSize: 11.5,
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  error: {
    padding: '10px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
  },
  empty: {
    padding: '24px 12px',
    textAlign: 'center',
    color: 'var(--fg-4)',
    fontSize: 13,
  },
};

export default RecentActivity;
